"use client";

import { useEffect, useState, type ReactNode } from "react";
import { X } from "lucide-react";
import { PrivacyContent } from "./PrivacyContent";
import { TermsContent } from "./TermsContent";

const DOCS = {
  terms: { title: "Terms & Conditions", href: "/terms", Content: TermsContent },
  privacy: { title: "Privacy Policy", href: "/privacy", Content: PrivacyContent },
} as const;

/** A link that opens the full terms / privacy text in a modal instead of navigating (href kept for new-tab / no-JS). */
export function LegalModalLink({ doc, children, className }: { doc: keyof typeof DOCS; children: ReactNode; className?: string }) {
  const [open, setOpen] = useState(false);
  const { title, href, Content } = DOCS[doc];

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && setOpen(false);
    document.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [open]);

  return (
    <>
      <a
        href={href}
        className={className}
        onClick={(e) => {
          if (e.metaKey || e.ctrlKey || e.shiftKey) return; // let "open in new tab" work
          e.preventDefault();
          setOpen(true);
        }}
      >
        {children}
      </a>
      {open && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center bg-ink/60 p-3 sm:p-6" onClick={() => setOpen(false)}>
          <div
            role="dialog"
            aria-modal="true"
            aria-label={title}
            onClick={(e) => e.stopPropagation()}
            className="flex max-h-full w-full max-w-3xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl"
          >
            <div className="flex items-center justify-between gap-3 border-b border-line px-5 py-4">
              <h2 className="font-display text-lg font-semibold text-ink">{title}</h2>
              <button type="button" onClick={() => setOpen(false)} aria-label="Close" className="rounded-full p-1.5 text-ink-soft hover:bg-cream">
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="overflow-y-auto px-5 py-5">
              <Content />
            </div>
            <div className="border-t border-line px-5 py-3 text-right">
              <button type="button" onClick={() => setOpen(false)} className="h-10 rounded-xl bg-brand-600 px-5 text-sm font-semibold text-white hover:bg-brand-700">
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
